/**
 * Generates a professional specification document (Cahier des Charges)
 * from a diagram's nodes and edges.
 * Written for non-technical clients — clear, structured, elegant.
 */

function groupBy(arr, fn) {
  return arr.reduce((acc, item) => {
    const key = fn(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

const TYPE_LABELS = {
  company: "Entite principale",
  subsidiary: "Module / Composant",
  person: "Acteur / Utilisateur",
  logo: "Technologie / Outil",
  service: "Service / API",
  job: "Role / Fonction",
  scenario: "Point de decision",
  note: "Note / Commentaire",
  metric: "Indicateur / KPI",
};

const DIAGRAM_TYPE_LABELS = {
  orgchart: "Organigramme d'entreprise",
  scenario: "Scénario conditionnel",
  process: "Processus métier",
};

export function generateSummary(title, diagramType, nodes, edges) {
  if (!nodes || nodes.length === 0) return "Aucun element dans le diagramme.";

  const lines = [];
  const grouped = groupBy(nodes, (n) => n.type || "company");
  const dateStr = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  const companies = grouped.company || [];
  const services = grouped.service || [];
  const subsidiaries = grouped.subsidiary || [];
  const persons = grouped.person || [];
  const scenarios = grouped.scenario || [];
  const metrics = grouped.metric || [];
  const logos = grouped.logo || [];
  const notes = grouped.note || [];
  const jobs = grouped.job || [];

  // ─── PAGE DE GARDE ───
  lines.push(`# Cahier des Charges`);
  lines.push(`## ${title || "Projet sans titre"}`);
  lines.push("");
  lines.push(`> Type de schéma : ${DIAGRAM_TYPE_LABELS[diagramType] || diagramType}`);
  lines.push(`> Date : ${dateStr}`);
  lines.push("");
  lines.push("---");
  lines.push("");

  // ─── 1. INTRODUCTION ───
  lines.push("## 1. Introduction et contexte");
  lines.push("");
  lines.push(`Ce document présenté les specifications fonctionnelles du projet **${title || "sans titre"}**. Il a ete elabore à partir d'un schéma visuel qui decrit l'ensemble des composants, des acteurs impliques, et des interactions entre les differents éléments du système.`);
  lines.push("");
  lines.push(`L'objectif de ce cahier des charges est de fournir une vision claire et accessible du projet, aussi bien pour les équipes techniques que pour les decideurs, afin de garantir une comprehension partagee des enjeux et du perimetre fonctionnel.`);
  lines.push("");

  // ─── 2. PERIMETRE DU PROJET ───
  lines.push("## 2. Perimetre du projet");
  lines.push("");
  lines.push(`Le projet est compose de **${nodes.length} éléments** organises en **${edges.length} relations fonctionnelles**. Voici une synthese des differentes categories identifiees :`);
  lines.push("");

  const categories = [];
  if (companies.length > 0) categories.push(`**${companies.length}** entite${companies.length > 1 ? "s" : ""} principale${companies.length > 1 ? "s" : ""} (${companies.map(n => n.data.title).join(", ")})`);
  if (subsidiaries.length > 0) categories.push(`**${subsidiaries.length}** module${subsidiaries.length > 1 ? "s" : ""} ou composant${subsidiaries.length > 1 ? "s" : ""}`);
  if (services.length > 0) categories.push(`**${services.length}** service${services.length > 1 ? "s" : ""} technique${services.length > 1 ? "s" : ""}`);
  if (persons.length > 0) categories.push(`**${persons.length}** acteur${persons.length > 1 ? "s" : ""} ou utilisateur${persons.length > 1 ? "s" : ""}`);
  if (jobs.length > 0) categories.push(`**${jobs.length}** role${jobs.length > 1 ? "s" : ""} identifie${jobs.length > 1 ? "s" : ""}`);
  if (scénarios.length > 0) categories.push(`**${scénarios.length}** point${scénarios.length > 1 ? "s" : ""} de decision`);
  if (metrics.length > 0) categories.push(`**${metrics.length}** indicateur${metrics.length > 1 ? "s" : ""} de performance`);
  if (logos.length > 0) categories.push(`**${logos.length}** technologie${logos.length > 1 ? "s" : ""} ou outil${logos.length > 1 ? "s" : ""}`);

  categories.forEach(c => lines.push(`- ${c}`));
  lines.push("");

  // ─── 3. DESCRIPTION DETAILLEE ───
  lines.push("## 3. Description detaillee des composants");
  lines.push("");

  let sectionCount = 0;
  const typeOrder = ["company", "subsidiary", "service", "person", "job", "logo", "scenario", "metric"];

  for (const type of typeOrder) {
    const group = grouped[type];
    if (!group || group.length === 0) continue;
    sectionCount++;

    lines.push(`### 3.${sectionCount}. ${TYPE_LABELS[type] || type}`);
    lines.push("");

    for (const node of group) {
      const d = node.data;
      lines.push(`**${d.title}**`);

      if (d.description) {
        lines.push(`${d.description}`);
      }

      const details = [];
      if (d.calculatedAmount) {
        details.push(`Valeur : ${d.calculatedAmount}`);
      } else if (d.amount) {
        details.push(`Valeur : ${d.amount}`);
      }
      if (d.condition) {
        details.push(`Condition : si ${d.condition}`);
      }
      if (d.tags && d.tags.length > 0) {
        details.push(`Caracteristiques : ${d.tags.join(", ")}`);
      }
      if (d.customFields && d.customFields.length > 0) {
        d.customFields.forEach(f => details.push(`${f.key} : ${f.value}`));
      }
      if (d.formula?.type) {
        const formulaLabels = { SUM: "Somme", AVG: "Moyenne", COUNT: "Comptage", MIN: "Minimum", MAX: "Maximum", PERCENT: "Pourcentage" };
        details.push(`Calcul automatique : ${formulaLabels[d.formula.type] || d.formula.type}`);
      }

      // Connections
      const outEdges = edges.filter(e => e.source === node.id);
      const inEdges = edges.filter(e => e.target === node.id);
      if (outEdges.length > 0) {
        const targets = outEdges.map(e => {
          const t = nodes.find(n => n.id === e.target);
          return t ? `${t.data.title}${e.label ? ` (${e.label})` : ""}` : null;
        }).filter(Boolean);
        if (targets.length > 0) details.push(`Interagit avec : ${targets.join(", ")}`);
      }
      if (inEdges.length > 0) {
        const sources = inEdges.map(e => {
          const s = nodes.find(n => n.id === e.source);
          return s ? `${s.data.title}${e.label ? ` (${e.label})` : ""}` : null;
        }).filter(Boolean);
        if (sources.length > 0) details.push(`Depend de : ${sources.join(", ")}`);
      }

      if (details.length > 0) {
        details.forEach(det => lines.push(`- ${det}`));
      }
      lines.push("");
    }
  }

  // ─── 4. FLUX ET INTERACTIONS ───
  lines.push("## 4. Flux et interactions");
  lines.push("");

  if (edges.length === 0) {
    lines.push("Aucune interaction n'a ete definie entre les composants pour le moment.");
  } else {
    lines.push(`Le système comporte **${edges.length} interactions** entre ses differents composants. Voici le detail des flux identifies :`);
    lines.push("");
    lines.push("| Élément source | Élément cible | Nature de la relation |");
    lines.push("|---|---|---|");
    for (const edge of edges) {
      const src = nodes.find(n => n.id === edge.source);
      const tgt = nodes.find(n => n.id === edge.target);
      if (src && tgt) {
        lines.push(`| ${src.data.title} | ${tgt.data.title} | ${edge.label || "Relation directe"} |`);
      }
    }
  }
  lines.push("");

  // ─── 5. INDICATEURS DE PERFORMANCE ───
  if (metrics.length > 0) {
    lines.push("## 5. Indicateurs de performance (KPIs)");
    lines.push("");
    lines.push("Les indicateurs suivants permettent de mesurer la reussite et le bon fonctionnement du projet :");
    lines.push("");
    lines.push("| Indicateur | Objectif | Description |");
    lines.push("|---|---|---|");
    for (const m of metrics) {
      const val = m.data.calculatedAmount || m.data.amount || "A definir";
      lines.push(`| ${m.data.title} | ${val} | ${m.data.description || "A preciser"} |`);
    }
    lines.push("");
  }

  // ─── 6. SCENARIOS ET REGLES METIER ───
  if (scénarios.length > 0) {
    const secNum = metrics.length > 0 ? 6 : 5;
    lines.push(`## ${secNum}. Règles métier et scénarios`);
    lines.push("");
    lines.push("Le système intégré les règles de gestion suivantes, qui definissent le comportement attendu selon differentes situations :");
    lines.push("");
    for (const s of scenarios) {
      lines.push(`**${s.data.title}**`);
      lines.push(`Condition : *si ${s.data.condition || "condition a definir"}*`);
      const outE = edges.filter(e => e.source === s.id);
      if (outE.length > 0) {
        for (const e of outE) {
          const target = nodes.find(n => n.id === e.target);
          if (target) {
            lines.push(`- ${e.label || "Alors"} : ${target.data.title}`);
          }
        }
      }
      lines.push("");
    }
  }

  // ─── 7. STACK TECHNIQUE ───
  if (logos.length > 0) {
    const secNum = 5 + (metrics.length > 0 ? 1 : 0) + (scenarios.length > 0 ? 1 : 0);
    lines.push(`## ${secNum}. Environnement technique`);
    lines.push("");
    lines.push("Les technologies et outils suivants ont ete identifies pour la realisation du projet :");
    lines.push("");
    logos.forEach(l => {
      const desc = l.data.description ? ` — ${l.data.description}` : "";
      lines.push(`- **${l.data.title}**${desc}`);
    });
    lines.push("");
  }

  // ─── 8. NOTES ───
  if (notes.length > 0) {
    const secNum = 5 + (metrics.length > 0 ? 1 : 0) + (scenarios.length > 0 ? 1 : 0) + (logos.length > 0 ? 1 : 0);
    lines.push(`## ${secNum}. Notes et recommandations`);
    lines.push("");
    for (const n of notes) {
      if (n.data.title) lines.push(`**${n.data.title}**`);
      if (n.data.description) lines.push(n.data.description);
      lines.push("");
    }
  }

  // ─── FOOTER ───
  lines.push("---");
  lines.push("");
  lines.push(`*Document genere par CardFlow — ${dateStr} a ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}*`);

  return lines.join("\n");
}
